/**
 * Mobile entry point — mirrors main.ts but wires the mobile-specific UI.
 */

import "./ui/styles.css";
import "./ui/mobile.css";

import { AudioEngine } from "./audio/sources";
import { ALT_SCENE_LABELS, type AltSceneType } from "./viz/altScenes";
import { Renderer, type SceneType } from "./viz/renderer";
import { resolveSceneLink } from "./viz/scenes/sceneLinks";
import { initMobileUI } from "./ui/mobile-ui";
import { loadRunningParams, saveRunningParams } from "./presets/store";
import { registerPwa } from "./pwa";
import { BackgroundRenderer } from "./viz/background";
import { MusicRuntime } from "./music/runtime";
import { initMusicDebugToggle, readInitialDebugReactivityMode } from "./music/debugToggle";

function bootstrap(): void {
  const canvas = document.getElementById("viz");
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error("Missing #viz canvas element.");
  }

  const engine = new AudioEngine();
  const renderer = new Renderer(canvas);
  const params = new URLSearchParams(window.location.search);
  const musicDebugEnabled = params.get("music-debug") === "1" || params.get("fluid-debug") === "1";
  const musicRuntime = new MusicRuntime({
    mode: musicDebugEnabled ? readInitialDebugReactivityMode(params) : "legacy",
    qualityMode: "low",
    surface: "mobile",
    exposeGlobal: musicDebugEnabled,
  });

  // Restore last-saved look params so the scene opens in a familiar state.
  Object.assign(renderer.params, loadRunningParams());

  const sceneParam = params.get("scene");
  const sceneLink = resolveSceneLink(sceneParam);
  const allowedScenes = new Set<SceneType>(["bioclast", ...(Object.keys(ALT_SCENE_LABELS) as AltSceneType[])]);
  if (sceneLink) {
    Object.assign(renderer.params, sceneLink.values);
    renderer.sceneType = sceneLink.sceneType;
    if (!params.has("ui")) {
      document.body.classList.add("ui-hidden");
    }
  } else if (sceneParam && allowedScenes.has(sceneParam as SceneType)) {
    renderer.sceneType = sceneParam as SceneType;
  }

  if (params.get("ui") === "show" || params.get("ui") === "1") {
    document.body.classList.remove("ui-hidden");
  }

  // ---- Auto-detect quality tier on first launch ----
  // Phones get "mobile" (15K particles) by default; everything else stays
  // at whatever loadRunningParams returned (default "standard"). The user
  // can override anytime via the Performance section in the Look tab.
  if (!localStorage.getItem("pfx_qualityTier_set")) {
    const ua = navigator.userAgent.toLowerCase();
    const isPhone = /iphone|android.*mobile/.test(ua);
    if (isPhone) renderer.params.qualityTier = "mobile";
    localStorage.setItem("pfx_qualityTier_set", "1");
  }

  // Auto-save every second and on tab close.
  setInterval(() => saveRunningParams(renderer.params), 1000);
  window.addEventListener("pagehide", () => saveRunningParams(renderer.params));

  // ---- Backdrop + HUD tick ----
  const backdropEl = document.getElementById("backdrop");
  const background = backdropEl ? new BackgroundRenderer(backdropEl) : null;
  const hudBpm = document.getElementById("hudBpm");
  const hudBass = document.getElementById("hudBass");
  const hudMid = document.getElementById("hudMid");
  const hudTreble = document.getElementById("hudTreble");

  const tick = (): void => {
    const f = engine.read();
    musicRuntime.updateFromLegacy(f, { source: engine.info() });
    renderer.setFeatures(f);

    if (backdropEl) {
      const bd = renderer.backdrop;
      if (background) {
        background.tick(f, bd);
      } else {
        backdropEl.style.background = `radial-gradient(ellipse at 50% 45%, rgba(${bd.r}, ${bd.g}, ${bd.b}, ${bd.alpha.toFixed(3)}) 0%, #010104 70%, #000 100%)`;
        backdropEl.style.opacity = String(Math.min(1, bd.alpha + 0.25));
        backdropEl.style.filter = `blur(${bd.blurPx}px)`;
      }
    }

    if (hudBpm) {
      hudBpm.textContent = f.bpm > 0 ? Math.round(f.bpm).toString() : "---";
      hudBpm.classList.toggle("beat", f.beat > 0.4);
    }
    if (hudBass) hudBass.style.setProperty("--lvl", `${Math.round(f.bass * 100)}%`);
    if (hudMid) hudMid.style.setProperty("--lvl", `${Math.round(f.mid * 100)}%`);
    if (hudTreble) hudTreble.style.setProperty("--lvl", `${Math.round(f.treble * 100)}%`);

    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  renderer.start();
  initMobileUI({ engine, renderer });
  initMusicDebugToggle({ enabled: musicDebugEnabled, runtime: musicRuntime, surface: "mobile" });
}

bootstrap();
registerPwa();
